import { Recipe, RecipeCreatedByValue } from "../../../api/recipeSimulator";
import { RecipeTypeClassification } from "../../../config/enums/recipeSimulator.enum";
import { needsRecalculation } from "../../../helpers/RecipeSimulatorHelper";
import { getImpactChangePercentage } from "../../../utils/utils";
import { RecipeGroupRecord, RecipeRecord, RecipeVariantRecord, SortField } from "./types";

interface RecipeCreatorFields {
  createdByName?: string;
  createdBy?: RecipeCreatedByValue;
  createdById?: string;
  userFirstName?: string;
  userLastName?: string;
}

const isRecipeCreatedByObject = (
  value: RecipeCreatedByValue | undefined
): value is { id: string; firstName?: string; lastName?: string } =>
  typeof value === "object" && value !== null && "id" in value;

export const getRecipeCreatedById = (recipe: RecipeCreatorFields): string | undefined => {
  if (recipe.createdById?.trim()) {
    return recipe.createdById.trim();
  }

  if (isRecipeCreatedByObject(recipe.createdBy)) {
    return recipe.createdBy.id;
  }

  if (typeof recipe.createdBy === "string" && recipe.createdBy.trim()) {
    return recipe.createdBy.trim();
  }

  return undefined;
};

export const getRecipeCreatedByName = (recipe: RecipeCreatorFields): string => {
  if (recipe.createdByName?.trim()) {
    return recipe.createdByName.trim();
  }

  if (isRecipeCreatedByObject(recipe.createdBy)) {
    const fullName = [recipe.createdBy.firstName, recipe.createdBy.lastName]
      .map(value => value?.trim())
      .filter(Boolean)
      .join(" ")
      .trim();

    if (fullName) {
      return fullName;
    }
  }

  const legacyFullName = [recipe.userFirstName, recipe.userLastName]
    .map(value => value?.trim())
    .filter(Boolean)
    .join(" ")
    .trim();

  if (legacyFullName) {
    return legacyFullName;
  }

  const createdById = getRecipeCreatedById(recipe);

  if (
    typeof recipe.createdBy === "string" &&
    recipe.createdBy.trim() &&
    recipe.createdBy.trim() !== createdById
  ) {
    return recipe.createdBy.trim();
  }

  return "";
};

export const CREATED_BY_DISPLAY_LENGTH = 4;

export const truncateCreatedByName = (
  name: string,
  maxLength = CREATED_BY_DISPLAY_LENGTH
): string => {
  const trimmed = name.trim();

  if (!trimmed) {
    return "-";
  }

  if (trimmed.length <= maxLength) {
    return trimmed;
  }

  return `${trimmed.slice(0, maxLength)}...`;
};

export const shouldShowCreatedByTooltip = (
  name: string,
  maxLength = CREATED_BY_DISPLAY_LENGTH
): boolean => name.trim().length > maxLength;

export const isDemoRecipe = (recipe: RecipeRecord) =>
  recipe.recipeTypeClassification?.toUpperCase() === RecipeTypeClassification.DEMOKIT;

export const isPublicRecipe = (recipe: RecipeRecord) =>
  recipe.recipeTypeClassification === RecipeTypeClassification.PUBLIC || isDemoRecipe(recipe);

export const isStrictlyPublicRecipe = (recipe: RecipeRecord) =>
  recipe.recipeTypeClassification === RecipeTypeClassification.PUBLIC;

export const isDeletedRecipe = (recipe: RecipeRecord) => {
  const status = recipe.status?.toUpperCase();
  return status === "DELETED" || status === "DELETE";
};

export const isSuccessfullyCalculated = (recipe: RecipeRecord) => Boolean(recipe.calculatedAt);

export const isOwnRecipe = (recipe: RecipeRecord, currentUserId?: string) => {
  if (!currentUserId) {
    return false;
  }

  const ownerId = getRecipeCreatedById(recipe);
  if (ownerId) {
    return String(ownerId) === String(currentUserId);
  }

  return recipe.isOwner === true;
};

export const hasMultipleVariants = (recipe: RecipeGroupRecord) => recipe.variants.length > 1;

export const toRecipeRecord = (
  group: RecipeGroupRecord,
  variant: RecipeVariantRecord = group.variants[0]
): RecipeRecord => ({
  id: group.id,
  variantId: variant.variantId,
  recipeName: group.recipeName,
  variationName: variant.variationName,
  baselineImpact: variant.baselineImpact,
  variationImpact: variant.variationImpact,
  impactChange: variant.impactChange,
  recipeTypeClassification: group.recipeTypeClassification,
  status: group.status,
  createdById: group.createdById,
  createdByName: group.createdByName,
  isOwner: group.isOwner,
  calculatedAt: group.calculatedAt,
  updatedDate: group.updatedDate,
  calculatedDate: group.calculatedDate,
  needsRecalculation: group.needsRecalculation
});

const getSortValue = (record: RecipeGroupRecord, sortField: SortField): string | number => {
  const primaryVariant = record.variants[0];

  if (sortField === "recipeName") {
    return record.recipeName;
  }

  if (sortField === "recipeTypeClassification") {
    return record.recipeTypeClassification ?? "";
  }

  if (sortField === "createdByName") {
    return record.createdByName ?? "";
  }

  if (sortField === "updatedDate") {
    return record.updatedDate;
  }

  if (sortField === "calculatedDate") {
    return record.calculatedDate;
  }

  if (!primaryVariant) {
    return "";
  }

  if (sortField === "variationName") {
    return hasMultipleVariants(record) ? "Multiple" : primaryVariant.variationName;
  }

  return primaryVariant[sortField];
};

export const mapRecipesToGroupRecords = (recipes: Recipe[] = []): RecipeGroupRecord[] =>
  recipes.map(recipe => {
    const baselineImpact = recipe.originalRecipeclimateChange || 0;
    const variants = recipe.variants?.length
      ? recipe.variants
      : [{ _id: "", variantName: "Base line", variantclimateChange: baselineImpact }];

    const recalculationData = {
      last_updated: recipe.updatedAt || null,
      last_calculated: recipe.calculatedAt || null
    };

    return {
      id: recipe._id,
      recipeName: recipe.recipeName,
      variants: variants.map(variant => ({
        variantId: variant._id,
        variationName: variant.variantName,
        baselineImpact,
        variationImpact: variant.variantclimateChange || 0,
        impactChange: baselineImpact
          ? getImpactChangePercentage(baselineImpact, variant.variantclimateChange ?? 0)
          : 0
      })),
      recipeTypeClassification: recipe.recipeTypeClassification,
      status: recipe.status,
      createdById: getRecipeCreatedById(recipe),
      createdByName: getRecipeCreatedByName(recipe),
      isOwner: recipe.isOwner,
      calculatedAt: recipe.calculatedAt ?? null,
      updatedDate: recipe.updatedAt ? new Date(recipe.updatedAt).toLocaleDateString() : "",
      calculatedDate: recipe.calculatedAt ? new Date(recipe.calculatedAt).toLocaleDateString() : "",
      needsRecalculation: needsRecalculation(recalculationData)
    };
  });

export const sortRecipeGroupRecords = (
  records: RecipeGroupRecord[],
  sortField: SortField,
  sortAsc: boolean
): RecipeGroupRecord[] =>
  [...records].sort((a, b) => {
    const aVal = getSortValue(a, sortField);
    const bVal = getSortValue(b, sortField);

    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortAsc ? aVal - bVal : bVal - aVal;
    }

    return sortAsc
      ? String(aVal ?? "").localeCompare(String(bVal ?? ""))
      : String(bVal ?? "").localeCompare(String(aVal ?? ""));
  });
